import React from 'react';
import { useNavigate } from 'react-router-dom';

// Palette maps a per-feature accent color to the classes used on the icon
// badge + hover ring. Tailwind can't compile fully dynamic class names, so
// map colors → explicit class strings.
const PALETTE = {
  blue:   { badge: 'bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400',       ring: 'hover:border-blue-300 dark:hover:border-blue-800' },
  red:    { badge: 'bg-red-50 dark:bg-red-950/40 text-red-600 dark:text-red-400',           ring: 'hover:border-red-300 dark:hover:border-red-800' },
  amber:  { badge: 'bg-amber-50 dark:bg-amber-950/40 text-amber-600 dark:text-amber-400',   ring: 'hover:border-amber-300 dark:hover:border-amber-800' },
  teal:   { badge: 'bg-teal-50 dark:bg-teal-950/40 text-teal-600 dark:text-teal-400',       ring: 'hover:border-teal-300 dark:hover:border-teal-800' },
  purple: { badge: 'bg-purple-50 dark:bg-purple-950/40 text-purple-600 dark:text-purple-400', ring: 'hover:border-purple-300 dark:hover:border-purple-800' },
  emerald:{ badge: 'bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400', ring: 'hover:border-emerald-300 dark:hover:border-emerald-800' },
  slate:  { badge: 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300',     ring: 'hover:border-slate-300 dark:hover:border-slate-700' },
};

export default function FeatureTile({
  title,
  icon,
  color = 'slate',
  primary,       // { value, label, subtitle? }
  secondary = [], // [{ value, label }, ...]
  ctaLabel,
  ctaTo,
  loading = false,
  error = null,
}) {
  const navigate = useNavigate();
  const go = () => { if (ctaTo) navigate(ctaTo); };
  const handleKey = (e) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); go(); }
  };
  const c = PALETTE[color] || PALETTE.slate;

  const ariaSummary = primary ? `${title}: ${primary.label} ${primary.value}` : title;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={go}
      onKeyDown={handleKey}
      className={`group relative bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5 cursor-pointer transition-all shadow-sm hover:shadow-md ${c.ring} focus:outline-none focus-visible:ring-2 focus-visible:ring-[#32B1FF] focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-950`}
      aria-label={`${ariaSummary}. Press to open ${title}`}
    >
      {/* header — icon badge + title */}
      <div className="flex items-center gap-3 mb-4">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${c.badge}`}>
          {icon}
        </div>
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
      </div>

      {error ? (
        <div className="text-xs text-gray-500 dark:text-gray-400 py-6">
          Unavailable right now. Open the full page for details.
        </div>
      ) : loading ? (
        <div className="animate-pulse py-2">
          <div className="h-8 w-24 rounded bg-gray-200 dark:bg-gray-800 mb-2" />
          <div className="h-3 w-32 rounded bg-gray-200 dark:bg-gray-800 mb-4" />
          <div className="flex gap-4">
            <div className="h-4 w-16 rounded bg-gray-200 dark:bg-gray-800" />
            <div className="h-4 w-16 rounded bg-gray-200 dark:bg-gray-800" />
          </div>
        </div>
      ) : (
        <>
          {/* Hero KPI */}
          {primary && (
            <div className="mb-4">
              <div className="text-3xl font-bold text-gray-900 dark:text-gray-100 tabular-nums leading-none">{primary.value}</div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">{primary.label}</div>
              {primary.subtitle && (
                <div className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5">{primary.subtitle}</div>
              )}
            </div>
          )}

          {/* Supporting KPIs */}
          {secondary.length > 0 && (
            <div className="grid grid-cols-2 gap-3 pt-3 border-t border-gray-100 dark:border-gray-800">
              {secondary.map((kpi, i) => (
                <div key={i}>
                  <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 tabular-nums">{kpi.value}</div>
                  <div className="text-[11px] text-gray-500 dark:text-gray-400 uppercase tracking-wide">{kpi.label}</div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* CTA */}
      <div className="mt-5 flex items-center justify-end">
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); go(); }}
          className="inline-flex items-center gap-1 text-sm font-medium text-[#32B1FF] hover:underline focus:outline-none focus-visible:underline"
        >
          {ctaLabel}
          <svg className="w-4 h-4 transition-transform group-hover:translate-x-0.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
    </div>
  );
}
